/**
 * OPL Issue Card - displays issue detail with edit/comment/attachment/log capabilities.
 * Supports rich text (images, links) for description and comments via Quill editor.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Avatar,
  Button,
  Card,
  Collapse,
  Descriptions,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Spin,
  Timeline,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  HistoryOutlined,
  MessageOutlined,
  PaperClipOutlined,
  SaveOutlined,
  UploadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import type {
  IssuePriority,
  OplAttachment,
  OplComment,
  OplIssueDetail,
  OplVersionItem,
  OperationLog,
} from '@/types';
import * as oplApi from '@/api/opl';
import IssueStatusTag from '@/components/opl/IssueStatusTag';
import PriorityTag from '@/components/opl/PriorityTag';
import UserSelector from '@/components/opl/UserSelector';
import RichTextEditor, { isContentEmpty, refreshImageTokens } from '@/components/common/RichTextEditor';
import { formatDate, formatFileSize } from '@/utils';

const { Text, Link } = Typography;

/** Format relative time in Chinese, e.g. "3 天前" */
function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return '刚刚';
  if (minutes < 60) return `${minutes} 分钟前`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} 小时前`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} 天前`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} 个月前`;
  return `${Math.floor(months / 12)} 年前`;
}

interface OplIssueCardProps {
  issue: OplIssueDetail;
  projectId: number;
  onRefresh: () => void;
}

const OplIssueCard: React.FC<OplIssueCardProps> = ({
  issue,
  projectId,
  onRefresh,
}) => {
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [descUploading, setDescUploading] = useState(false);
  const [commentUploading, setCommentUploading] = useState(false);

  // Edit form state
  const [description, setDescription] = useState(issue.description);
  const [bswVersion, setBswVersion] = useState(issue.bsw_version || '');
  const [priority, setPriority] = useState<IssuePriority | null>(issue.priority);
  const [impact, setImpact] = useState(issue.impact || '');
  const [issueStatus, setIssueStatus] = useState(issue.status);
  const [closedVersion, setClosedVersion] = useState(issue.closed_version || '');
  const [owner, setOwner] = useState<number | null>(issue.owner?.id || null);

  // Versions for close
  const [versions, setVersions] = useState<OplVersionItem[]>([]);

  // Comments
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);

  // Attachments
  const [uploading, setUploading] = useState(false);

  // Operation logs
  const [logModalOpen, setLogModalOpen] = useState(false);
  const [logs, setLogs] = useState<OperationLog[]>([]);
  const [logsLoading, setLogsLoading] = useState(false);

  // Reset form when issue changes
  useEffect(() => {
    setDescription(issue.description);
    setBswVersion(issue.bsw_version || '');
    setPriority(issue.priority);
    setImpact(issue.impact || '');
    setIssueStatus(issue.status);
    setClosedVersion(issue.closed_version || '');
    setOwner(issue.owner?.id || null);
    setEditing(false);
  }, [issue]);

  // Fetch versions for resolved-version selector and view-mode link
  useEffect(() => {
    oplApi.getVersions(projectId).then(setVersions).catch(() => {});
  }, [projectId]);

  const handleSave = async () => {
    if (isContentEmpty(description)) {
      message.error('问题描述不能为空');
      return;
    }
    if (descUploading) {
      message.warning('图片上传中，请稍候...');
      return;
    }
    setSaving(true);
    try {
      await oplApi.updateIssue(projectId, issue.id, {
        description,
        bsw_version: bswVersion || undefined,
        priority: priority || undefined,
        impact: impact || undefined,
        status: issueStatus,
        closed_version: closedVersion || undefined,
        owner_id: owner || undefined,
        updated_at: issue.updated_at,
      });
      message.success('保存成功');
      setEditing(false);
      onRefresh();
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { message?: string } } })?.response;
      if (resp?.status === 409) {
        message.error('该记录已被其他用户修改，请刷新后重试');
        onRefresh();
      } else {
        message.error(resp?.data?.message || '保存失败');
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await oplApi.deleteIssue(projectId, issue.id);
      message.success('删除成功');
      onRefresh();
    } catch {
      message.error('删除失败');
    }
  };

  // Comments
  const handleAddComment = async () => {
    if (isContentEmpty(newComment)) return;
    if (commentUploading) {
      message.warning('图片上传中，请稍候...');
      return;
    }
    setSubmittingComment(true);
    try {
      await oplApi.createComment(projectId, issue.id, newComment);
      setNewComment('');
      message.success('评论添加成功');
      onRefresh();
    } catch {
      message.error('评论添加失败');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    try {
      await oplApi.deleteComment(projectId, issue.id, commentId);
      message.success('评论已删除');
      onRefresh();
    } catch {
      message.error('评论删除失败');
    }
  };

  // Attachments
  const handleUploadAttachment = async (file: File) => {
    setUploading(true);
    try {
      const { getUploadUrl } = await import('@/api/storage');
      const { url, object_key } = await getUploadUrl(file.name, file.type || 'application/octet-stream');

      // Upload file to OBS
      const response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });

      // Check upload status
      if (!response.ok) {
        throw new Error(`附件上传失败: ${response.status} ${response.statusText}`);
      }

      // Verify ETag exists (basic integrity check)
      const etag = response.headers.get('ETag');
      if (!etag) {
        throw new Error('附件上传失败: 未收到OBS确认');
      }

      await oplApi.createAttachment(projectId, issue.id, {
        file_name: file.name,
        file_url: url.split('?')[0],
        object_key,
        file_size: file.size,
      });
      message.success('附件上传成功');
      onRefresh();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '附件上传失败';
      message.error(errorMessage);
    } finally {
      setUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachment: OplAttachment) => {
    try {
      const { getDownloadUrl } = await import('@/api/storage');
      const { url } = await getDownloadUrl(attachment.object_key);
      window.open(url, '_blank');
    } catch {
      message.error('获取下载链接失败');
    }
  };

  const handleDeleteAttachment = async (attachmentId: number) => {
    try {
      await oplApi.deleteAttachment(projectId, issue.id, attachmentId);
      message.success('附件已删除');
      onRefresh();
    } catch {
      message.error('附件删除失败');
    }
  };

  // Logs
  const handleShowLogs = async () => {
    setLogModalOpen(true);
    setLogsLoading(true);
    try {
      const result = await oplApi.getIssueLogs(projectId, issue.id);
      setLogs(result.logs);
    } catch {
      message.error('获取操作日志失败');
    } finally {
      setLogsLoading(false);
    }
  };

  return (
    <>
      <Card
        className="mb-4"
        title={
          <Space>
            <Text strong>{issue.issue_id}</Text>
            <IssueStatusTag status={issue.status} />
            {issue.priority && <PriorityTag priority={issue.priority} />}
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<HistoryOutlined />}
              size="small"
              onClick={handleShowLogs}
            >
              操作日志
            </Button>
            {editing ? (
              <>
                <Button
                  type="primary"
                  icon={<SaveOutlined />}
                  size="small"
                  loading={saving}
                  onClick={handleSave}
                >
                  保存
                </Button>
                <Button size="small" onClick={() => setEditing(false)}>
                  取消
                </Button>
              </>
            ) : (
              <Button
                icon={<EditOutlined />}
                size="small"
                onClick={() => setEditing(true)}
              >
                编辑
              </Button>
            )}
            <Popconfirm title="确认删除该问题？" onConfirm={handleDelete}>
              <Button danger icon={<DeleteOutlined />} size="small">
                删除
              </Button>
            </Popconfirm>
          </Space>
        }
      >
        {/* Issue details */}
        {editing ? (
          <div className="space-y-4">
            <div>
              <Text type="secondary">问题描述</Text>
              <RichTextEditor
                value={description}
                onChange={setDescription}
                placeholder="请输入问题描述... 支持粘贴图片和超链接"
                onUploadingChange={setDescUploading}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Text type="secondary">底软版本</Text>
                <Input
                  value={bswVersion}
                  onChange={(e) => setBswVersion(e.target.value)}
                  placeholder="底软版本"
                />
              </div>
              <div>
                <Text type="secondary">优先级</Text>
                <Select
                  className="w-full"
                  value={priority}
                  onChange={setPriority}
                  allowClear
                  options={[
                    { value: 'p0', label: 'P0-紧急' },
                    { value: 'p1', label: 'P1-高' },
                    { value: 'p2', label: 'P2-中' },
                    { value: 'p3', label: 'P3-低' },
                  ]}
                />
              </div>
              <div>
                <Text type="secondary">状态</Text>
                <Select
                  className="w-full"
                  value={issueStatus}
                  onChange={setIssueStatus}
                  options={[
                    { value: 'opened', label: '开放' },
                    { value: 'closed', label: '已关闭' },
                  ]}
                />
              </div>
            </div>
            <div>
              <Text type="secondary">问题影响</Text>
              <Input.TextArea
                rows={2}
                value={impact}
                onChange={(e) => setImpact(e.target.value)}
                placeholder="问题影响"
              />
            </div>
            <div>
              <Text type="secondary">问题解决版本</Text>
              <Select
                className="w-full"
                value={closedVersion || undefined}
                onChange={setClosedVersion}
                allowClear
                placeholder="选择问题解决版本"
                options={versions.map((v) => ({
                  value: v.name,
                  label: v.name,
                }))}
              />
            </div>
            <div>
              <Text type="secondary">负责人</Text>
              <UserSelector
                value={owner}
                onChange={setOwner}
                placeholder="选择负责人"
              />
            </div>
          </div>
        ) : (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="问题ID">{issue.issue_id}</Descriptions.Item>
            <Descriptions.Item label="状态">
              <IssueStatusTag status={issue.status} />
            </Descriptions.Item>
            <Descriptions.Item label="优先级">
              <PriorityTag priority={issue.priority} />
            </Descriptions.Item>
            <Descriptions.Item label="底软版本">
              {issue.bsw_version || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建人">
              {issue.created_by?.display_name || issue.created_by?.username}
            </Descriptions.Item>
            <Descriptions.Item label="负责人">
              {issue.owner?.display_name || issue.owner?.username || '-'}
            </Descriptions.Item>
            <Descriptions.Item label="创建时间">
              {formatDate(issue.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="更新时间">
              {formatDate(issue.updated_at)}
            </Descriptions.Item>
            <Descriptions.Item label="问题描述" span={2}>
              <div
                className="ql-editor"
                style={{ padding: 0, margin: 0 }}
                dangerouslySetInnerHTML={{ __html: refreshImageTokens(issue.description) }}
              />
            </Descriptions.Item>
            {issue.impact && (
              <Descriptions.Item label="问题影响" span={2}>
                <div style={{ whiteSpace: 'pre-wrap' }}>{issue.impact}</div>
              </Descriptions.Item>
            )}
            <Descriptions.Item label="问题解决版本" span={2}>
              {issue.closed_version ? (() => {
                const pkg = versions.find((v) => v.name === issue.closed_version);
                return pkg ? (
                  <a onClick={() => navigate(`/package/${pkg.id}`)}>{issue.closed_version}</a>
                ) : issue.closed_version;
              })() : '-'}
            </Descriptions.Item>
          </Descriptions>
        )}

        {/* Comments & Attachments in Collapse panels */}
        <Collapse
          ghost
          size="small"
          className="mt-2"
          items={[
            {
              key: 'comments',
              label: (
                <Space>
                  <MessageOutlined />
                  评论 {(issue.comments?.length || 0) > 0 && `(${issue.comments!.length})`}
                </Space>
              ),
              children: (
                <div>
                  {/* New comment input */}
                  <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
                    <Avatar size="small" icon={<UserOutlined />} style={{ flexShrink: 0, marginTop: 4 }} />
                    <div style={{ flex: 1 }}>
                      <RichTextEditor
                        value={newComment}
                        onChange={setNewComment}
                        placeholder="输入评论... 支持粘贴图片和超链接"
                        onUploadingChange={setCommentUploading}
                      />
                      <Button
                        type="primary"
                        size="small"
                        onClick={handleAddComment}
                        loading={submittingComment}
                        disabled={isContentEmpty(newComment) || commentUploading}
                        style={{ marginTop: 8 }}
                      >
                        {commentUploading ? '图片上传中...' : '发送'}
                      </Button>
                    </div>
                  </div>

                  {/* Comment list */}
                  {!issue.comments?.length ? (
                    <Text type="secondary">暂无评论</Text>
                  ) : (
                    <div>
                      {issue.comments.map((comment: OplComment) => (
                        <div
                          key={comment.id}
                          style={{
                            display: 'flex',
                            gap: 8,
                            padding: '12px 0',
                            borderBottom: '1px solid #f0f0f0',
                          }}
                        >
                          <Avatar size="small" icon={<UserOutlined />} style={{ flexShrink: 0, marginTop: 2 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <Text strong style={{ fontSize: 13 }}>
                                {comment.created_by?.display_name || comment.created_by?.username}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                added a comment - {relativeTime(comment.created_at)}
                              </Text>
                              <Space size={4} style={{ marginLeft: 'auto' }}>
                                <Popconfirm
                                  title="确认删除？"
                                  onConfirm={() => handleDeleteComment(comment.id)}
                                >
                                  <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                                </Popconfirm>
                              </Space>
                            </div>
                            <div
                              className="comment-content ql-editor"
                              style={{ margin: 0, fontSize: 13, padding: 0 }}
                              dangerouslySetInnerHTML={{ __html: refreshImageTokens(comment.content) }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
            {
              key: 'attachments',
              label: (
                <Space>
                  <PaperClipOutlined />
                  附件 {(issue.attachments?.length || 0) > 0 && `(${issue.attachments!.length})`}
                </Space>
              ),
              children: (
                <div>
                  <Upload
                    multiple
                    beforeUpload={(file) => {
                      handleUploadAttachment(file);
                      return false;
                    }}
                    showUploadList={false}
                  >
                    <Button icon={<UploadOutlined />} loading={uploading} size="small" style={{ marginBottom: 12 }}>
                      上传附件
                    </Button>
                  </Upload>

                  {!issue.attachments?.length ? (
                    <Text type="secondary">暂无附件</Text>
                  ) : (
                    <div>
                      {issue.attachments.map((att: OplAttachment) => (
                        <div
                          key={att.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '8px 0',
                            borderBottom: '1px solid #f5f5f5',
                          }}
                        >
                          <PaperClipOutlined style={{ color: '#999', flexShrink: 0 }} />
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <Link
                                style={{ fontSize: 13 }}
                                onClick={() => handleDownloadAttachment(att)}
                                ellipsis
                              >
                                {att.file_name}
                              </Link>
                              {att.file_size > 0 && (
                                <Text type="secondary" style={{ fontSize: 12, flexShrink: 0 }}>
                                  {formatFileSize(att.file_size)}
                                </Text>
                              )}
                            </div>
                            <Space size={8} style={{ fontSize: 12 }}>
                              <Text type="secondary">
                                {att.created_by?.display_name || att.created_by?.username}
                              </Text>
                              <Text type="secondary">
                                {new Date(att.created_at).toLocaleString('zh-CN')}
                              </Text>
                            </Space>
                          </div>
                          <Popconfirm
                            title="确认删除？"
                            onConfirm={() => handleDeleteAttachment(att.id)}
                          >
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ),
            },
          ]}
        />
      </Card>

      {/* Operation Log Modal */}
      <Modal
        title="操作日志"
        open={logModalOpen}
        onCancel={() => setLogModalOpen(false)}
        footer={null}
        width={700}
      >
        {logsLoading ? (
          <div className="text-center py-8">
            <Spin />
          </div>
        ) : (
          <Timeline
            items={logs.map((log) => ({
              children: (
                <div>
                  <Text strong>
                    {log.operator?.display_name || log.operator?.username || '系统'}
                  </Text>
                  <Text type="secondary" className="ml-2">
                    {log.action_display || log.action}
                  </Text>
                  <Text type="secondary" className="ml-2">
                    {formatDate(log.created_at)}
                  </Text>
                  {log.description && (
                    <div className="text-gray-500 text-sm">{log.description}</div>
                  )}
                </div>
              ),
            }))}
          />
        )}
      </Modal>
    </>
  );
};

export default OplIssueCard;
